import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import ProcessGraphLink from "./ProcessGraphLink";
import { formatDuration } from "../../utils";
import { appScrollElement } from "../../utils/appScroll";
import { getAllocationColor, appPrimaryColor, INSIGNIFICANT_COLOR } from "../analytics/palette";
import AllocationDonut from "../analytics/AllocationDonut";
import ProcessAllocationBars from "./ProcessAllocationBars";
import { appIconUri } from "../processes/appIcon";
import type { ProcessOverview } from "./processOverview";
import { dashboardDateFormatter } from "./dateSelection";

export default function OverviewAllocation({
  onSelectionChange,
  metrics,
  personal = false,
  previousMetrics,
  comparison,
  linkUserIds,
}: {
  onSelectionChange?: (name: string | null) => void;
  metrics: ProcessOverview;
  personal?: boolean;
  previousMetrics?: ProcessOverview;
  comparison?: { from: string; to: string; days: number };
  linkUserIds: string[] | "all";
}) {
  const { t, i18n } = useTranslation();
  const [selectedSlug, setSelectedSlugState] = useState<string | null>(null);
  const setSelectedSlug = (value: React.SetStateAction<string | null>) => {
    const next = typeof value === "function" ? value(selectedSlug) : value;
    setSelectedSlugState(next);
    onSelectionChange?.(metrics.processes.find(row => row.slug === next)?.name ?? null);
  };
  const selected = metrics.processes.find(row => row.slug === selectedSlug);
  const processListRef = useRef<HTMLElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const scroller = appScrollElement();
    const summary = summaryRef.current;
    const list = processListRef.current;
    if (!selectedSlug || !scroller || !summary || !list) return;
    const bounds = summary.getBoundingClientRect();
    // Side-by-side previews should never move the list away from the user's pointer.
    if (bounds.left >= list.getBoundingClientRect().right) return;
    const viewport = scroller.getBoundingClientRect();
    const top = Math.max(0, viewport.top) + 12;
    const bottom = Math.min(window.innerHeight, viewport.bottom) - 12;
    const delta =
      bounds.top < top || bounds.height > bottom - top
        ? bounds.top - top
        : Math.max(0, bounds.bottom - bottom);
    if (delta)
      scroller.scrollBy({
        top: delta,
        behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
      });
  }, [selectedSlug]);
  const apps = selected ? selected.apps : metrics.apps;
  const processSeconds = selected ? selected.seconds : metrics.seconds;
  const noAppSeconds = Math.max(
    0,
    processSeconds - apps.reduce((sum, row) => sum + row.seconds, 0),
  );
  // A process absent from the previous period had no app time then.
  const previousApps = selected
    ? previousMetrics &&
      (previousMetrics.processes.find(row => row.slug === selected.slug)?.apps ?? [])
    : previousMetrics?.apps;
  const formatDate = dashboardDateFormatter(
    comparison ? [comparison.from, comparison.to] : [],
    i18n?.resolvedLanguage,
  );
  const comparisonDates = comparison && {
    from: formatDate(comparison.from),
    to: formatDate(comparison.to),
    count: comparison.days,
  };
  const comparisonLabel = comparisonDates && t("dashboard.time_change_help", comparisonDates);
  const comparisonHelp = comparisonDates && t("dashboard.delta_previous_help", comparisonDates);

  return (
    <div className="grid items-start gap-5 @[1200px]:grid-cols-2 @[1200px]:items-stretch">
      <section
        ref={processListRef}
        className="flex min-w-0 flex-col rounded-xl border border-outline bg-surface p-5 @[600px]:p-6"
        aria-labelledby="process-allocation-title"
      >
        <h2
          id="process-allocation-title"
          className="font-display text-xl font-semibold tracking-tight"
        >
          {t(personal ? "dashboard.my_processes" : "dashboard.overview_processes")}
        </h2>
        <p className="mt-1 mb-4 text-sm text-text-mid">{t("dashboard.select_process_hint")}</p>
        <ProcessAllocationBars
          processes={metrics.processes}
          previousProcesses={previousMetrics?.processes}
          comparisonLabel={comparisonLabel}
          comparisonHelp={comparisonHelp}
          selectedSlug={selectedSlug}
          onSelect={slug => setSelectedSlug(current => (current === slug ? null : slug))}
          userIds={linkUserIds}
        />
      </section>
      <section
        className="@container/selection flex min-w-0 flex-col rounded-xl border border-outline bg-surface p-5 @[600px]:p-6"
        aria-labelledby={selected ? "selected-process-title" : "app-allocation-title"}
      >
        {selected ? (
          <div
            ref={summaryRef}
            role="group"
            aria-label={t("dashboard.selected_process")}
            className="mb-4"
          >
            <p className="mb-1 text-xs font-medium text-text-mid">
              {t("dashboard.selected_process")}
            </p>
            <div className="flex items-start justify-between gap-3">
              <h2
                id="selected-process-title"
                className="flex min-w-0 flex-1 items-start gap-2.5 font-display text-xl font-semibold leading-snug tracking-tight"
              >
                <span
                  className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full"
                  aria-hidden="true"
                  style={{
                    backgroundColor: getAllocationColor(
                      metrics.processes.findIndex(row => row.slug === selected.slug),
                    ),
                  }}
                />
                <span className="min-w-0 break-words">{selected.name}</span>
              </h2>
              <div className="flex shrink-0 items-center gap-1">
                <ProcessGraphLink
                  slug={selected.slug}
                  name={selected.name}
                  userIds={linkUserIds}
                  showLabel
                  labelClassName="hidden @[640px]/selection:inline"
                />
                <button
                  type="button"
                  onClick={() => setSelectedSlug(null)}
                  aria-label={t("dashboard.clear_selection")}
                  title={t("dashboard.clear_selection")}
                  className="rounded-lg p-2 text-text-mid transition-colors hover:bg-muted-surface hover:text-text focus-visible:outline-info"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-4 rounded-lg bg-muted-surface p-3 @[440px]/selection:grid-cols-4">
              {[
                [t("dashboard.total_time"), formatDuration(selected.seconds)],
                [t("dashboard.share"), `${Math.round(selected.share * 100)}%`],
                [t("dashboard.observations"), selected.count],
                [t("dashboard.avg_time"), formatDuration(selected.averageSeconds)],
              ].map(([label, value]) => (
                <div key={label} className="min-w-0">
                  <dt className="text-xs text-text-mid">{label}</dt>
                  <dd className="mt-1 font-display text-xl font-semibold tracking-tight tabular-nums">
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ) : (
          <>
            <h2
              id="app-allocation-title"
              className="font-display text-xl font-semibold tracking-tight"
            >
              {t(personal ? "dashboard.my_apps" : "dashboard.overview_apps")}
            </h2>
            <p className="mt-1 mb-4 text-sm text-text-mid">{t("dashboard.overview_apps_hint")}</p>
          </>
        )}
        {apps.length || processSeconds > 0 ? (
          <AllocationDonut
            key={selected?.slug ?? "all"}
            data={apps.map(row => ({
              name: row.name,
              value: row.seconds,
              color: appPrimaryColor(row.name),
              iconUri: appIconUri(row.name),
            }))}
            previousData={previousApps?.map(row => ({ name: row.name, value: row.seconds }))}
            comparisonLabel={comparisonLabel}
            comparisonHelp={comparisonHelp}
            otherLabel={t("dashboard.other_apps")}
            additionalSlice={
              noAppSeconds > 0
                ? {
                    name: t("dashboard.no_app"),
                    value: noAppSeconds,
                    color: INSIGNIFICANT_COLOR,
                    hatched: true,
                    description: t("dashboard.no_app_help"),
                  }
                : undefined
            }
            expandLabel={t("dashboard.show_all_apps", { count: apps.length })}
          />
        ) : (
          <p className="flex min-h-[220px] items-center justify-center text-sm text-text-dim">
            {t("dashboard.empty_apps")}
          </p>
        )}
      </section>
    </div>
  );
}
